import React, {
  useRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react";
import { Swiper as SwiperReact, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

function Swiper({
  children,
  type = "navigation",
  responsive,
  centerSlides = false,
  enablePagination: customEnablePagination,
  enableNavigation: customEnableNavigation,
  onArrowsVisibilityChange,
  ...props
}) {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const swiperInstanceRef = useRef(null);
  const slides = React.Children.toArray(children);
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1440
  );
  const [showArrows, setShowArrows] = useState(true);
  const enableNavigation =
    customEnableNavigation ?? (type === "navigation" || type === "both");
  const enablePagination =
    customEnablePagination ?? (type === "pagination" || type === "both");
  const defaultBreakpoints = useMemo(
    () => ({
      320: 1,
      480: 1,
      768: 2,
      1024: 3,
      1440: 4,
    }),
    []
  );
  const getCurrentSlidesPerView = useCallback(() => {
    const breakpoints = responsive || defaultBreakpoints;
    const sortedBreakpoints = Object.entries(breakpoints)
      .map(([width, count]) => [parseInt(width), count])
      .sort((a, b) => b[0] - a[0]); // descending
    const currentBreakpoint = sortedBreakpoints.find(
      ([width]) => windowWidth >= width
    );
    return (
      currentBreakpoint?.[1] || sortedBreakpoints[sortedBreakpoints.length - 1][1]
    );
  }, [responsive, windowWidth, defaultBreakpoints]);
  const updateArrowsVisibility = useCallback(() => {
    const slideCount = slides.length;
    const currentSlidesPerView = getCurrentSlidesPerView();
    const shouldShowArrows = slideCount > currentSlidesPerView;
    setShowArrows(shouldShowArrows);
    onArrowsVisibilityChange?.(shouldShowArrows);
  }, [slides.length, getCurrentSlidesPerView, onArrowsVisibilityChange]);
  const handleResize = useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    updateArrowsVisibility();
  }, [updateArrowsVisibility]);

  const generateBreakpoints = useCallback(() => {
    const baseBreakpoints = {
      320: { slidesPerView: 1, centeredSlides: true },
      520: { slidesPerView: 1, centeredSlides: true },
      768: { slidesPerView: 2, centeredSlides: false },
      1024: { slidesPerView: 3, centeredSlides: false },
      1440: { slidesPerView: 4, centeredSlides: false },
    };

    return responsive
      ? Object.fromEntries(
          Object.entries(responsive).map(([width, count]) => [
            width,
            { slidesPerView: count, centeredSlides: false },
          ])
        )
      : baseBreakpoints;
  }, [responsive]);

  const initializeNavigation = useCallback(
    (swiper) => {
      const prevEl = prevRef.current;
      const nextEl = nextRef.current;
      if (swiper && prevEl && nextEl && enableNavigation) {
        swiper.params.navigation.prevEl = prevEl;
        swiper.params.navigation.nextEl = nextEl;
        if (!swiper.navigation.initialized) {
          swiper.navigation.init();
        }
        swiper.navigation.update();
      }
    },
    [enableNavigation]
  );
  const handleSwiperInit = useCallback(
    (swiper) => {
      swiperInstanceRef.current = swiper;
      initializeNavigation(swiper);
      updateArrowsVisibility();
    },
    [initializeNavigation, updateArrowsVisibility]
  );

  useEffect(() => {
    if (swiperInstanceRef.current) {
      initializeNavigation(swiperInstanceRef.current);
    }
  }, [initializeNavigation]);

  const navigationConfig = enableNavigation && {
    prevEl: prevRef.current,
    nextEl: nextRef.current,
  };
  const paginationConfig = enablePagination && {
    el: ".swiper__pagination",
    clickable: true,
    dynamicBullets: true,
    dynamicMainBullets: 3,
  };
  const modules = [
    ...(enableNavigation ? [Navigation] : []),
    ...(enablePagination ? [Pagination] : []),
    Autoplay,
  ];

  return (
    <div
      className={`swiper-container ${
        !showArrows ? "swiper-container--no-padding" : ""
      }`}
    >
      <SwiperReact
        modules={modules}
        autoplay={{
          delay: 8000,
          disableOnInteraction: false,
        }}
        spaceBetween={20}
        centerInsufficientSlides={centerSlides}
        navigation={navigationConfig}
        pagination={paginationConfig}
        loop={false}
        onInit={handleSwiperInit}
        onResize={updateArrowsVisibility}
        onBreakpoint={updateArrowsVisibility}
        breakpoints={generateBreakpoints()}
        {...props}
      >
        {slides.map((child, index) => (
          <SwiperSlide key={index}>{child}</SwiperSlide>
        ))}
      </SwiperReact>
      {enableNavigation && (
        <div className="swiper__arrows">
          <div className="swiper-button-prev" ref={prevRef}></div>
          <div className="swiper-button-next" ref={nextRef}></div>
        </div>
      )}
      {enablePagination && <div className="swiper__pagination"></div>}
    </div>
  );
}

export default React.memo(Swiper);
